import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { artistsApi } from "../../utils/api";
import { artistDeleteNeedsAttention, displayLabel } from "../../utils/helpers";

const PAGE_SIZE = 20;

/**
 * Soft-deleted artists.
 *
 * Deleting an artist keeps its document as suppression memory — an artist id is derived from the
 * provider's id, so a hard delete would be undone by the next fetch re-indexing the same id. This
 * is the only view those artists appear in; every other artist read filters them out.
 */
export default function DeletedArtistsView() {
  const [artists, setArtists] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();

  const load = useCallback(async (pageNum, searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const data = await artistsApi.getDeleted({
        page: pageNum,
        page_size: PAGE_SIZE,
        ...(searchTerm && { search: searchTerm }),
      });
      setArtists(data.artists || []);
      setTotal(data.pagination?.total || 0);
      setTotalPages(data.pagination?.total_pages || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load deleted artists.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, search);
  }, [load, page, search]);

  // The delete writes the `deleted` flag before it strips refs, so a cascade that dies
  // part-way leaves the artist here with references still on events and profiles. This tab is
  // the only place it still appears — every other artist read filters it out — so without a
  // retry the documented remedy ("repeat the call") would need a hand-made request. Repeating
  // it is safe: every step is idempotent and deleted_at/deleted_by keep their first values.
  const handleRetryCleanup = async (artist) => {
    setRetryingId(artist.id);
    setError(null);
    setNotice(null);
    try {
      const result = await artistsApi.delete(artist.id, { confirm: true });
      if (artistDeleteNeedsAttention(result)) {
        setError(result.message);
      } else {
        setNotice(`Cleanup finished for ${displayLabel(artist.preferred_name, artist.name) || artist.id}.`);
      }
      await load(page, search);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not finish the cleanup.");
    } finally {
      setRetryingId(null);
    }
  };

  const handleSearchSubmit = (e) => {
    setPage(1);
    submitSearch(e);
  };

  const handleSearchClear = () => {
    setPage(1);
    clearSearch();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Deleted artists</h2>
          <p className="text-sm text-muted-foreground">
            Their events, user favorites and reports were cleaned up on delete. The record is kept so ingestion cannot
            re-create the artist on the next fetch.
          </p>
        </div>
        <SearchFilterForm
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          placeholder="Search by name or ID"
          loading={loading}
          submitVariant="default"
          clearContent="Clear"
          inputClassName="sm:w-56"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {notice && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          {notice}
        </div>
      )}

      {!loading && (
        <>
          <div className="text-sm text-muted-foreground">
            {total === 0 ? "No deleted artists" : `${total} deleted artist${total !== 1 ? "s" : ""}`}
            {search && <span> matching &ldquo;{search}&rdquo;</span>}
          </div>

          {artists.length > 0 ? (
            <div className="rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Deleted at</TableHead>
                    <TableHead>Deleted by</TableHead>
                    <TableHead className="text-right">Cleanup</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {artists.map((artist) => (
                    <TableRow key={artist.id}>
                      <TableCell>
                        {displayLabel(artist.preferred_name, artist.name)}
                        <ProviderLabel preferred={artist.preferred_name} original={artist.name} />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{artist.id}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {artist.deleted_at ? new Date(artist.deleted_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {artist.deleted_by_email || artist.deleted_by || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRetryCleanup(artist)}
                          disabled={retryingId !== null}
                          title="Re-run the reference cleanup for this artist"
                        >
                          {retryingId === artist.id ? (
                            <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Cleaning...</>
                          ) : (
                            "Re-run cleanup"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <Trash2 className="mx-auto mb-3 h-8 w-8" />
              <p className="font-medium">Nothing deleted yet</p>
              <p className="mt-1 text-sm">Deleted artists will appear here.</p>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
